import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarDaysIcon } from 'lucide-react';
import { DataTable, EmptyState, ErrorNotice, Panel, Pill, SearchInput, SkeletonTable, type DataTableSort } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { eventStageTone } from '@/lib/event-status';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { CHARITY_OUTCOME_LABELS, charityOutcomeTone, listCharityEvents, type CharityEvent } from '@/lib/charities';

const CHARITY_EVENT_SORT_FIELDS = { date: 'dateMs', event: 'eventLabel', location: 'locationLabel', stage: 'stageLabel' } as const;
const CHARITY_EVENT_SORT_KEYS = Object.values(CHARITY_EVENT_SORT_FIELDS);
const DEFAULT_CHARITY_EVENT_SORT: DataTableSort = { key: CHARITY_EVENT_SORT_FIELDS.date, direction: 'desc' };

interface CharityEventRow extends CharityEvent {
  readonly dateMs: number | null;
  readonly dateLabel: string;
  readonly eventLabel: string;
  readonly locationLabel: string;
  readonly stageLabel: string;
}

function charityEventRow(event: CharityEvent): CharityEventRow {
  const parsed = event.eventDate ? Date.parse(event.eventDate) : NaN;
  const dateMs = Number.isNaN(parsed) ? null : parsed;
  return {
    ...event,
    dateMs,
    dateLabel: dateMs === null ? '—' : new Date(dateMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    eventLabel: [event.contractNumber, event.accountName].filter(Boolean).join(' · ') || event.programName || 'Untitled event',
    locationLabel: [event.city, event.state].filter(Boolean).join(', '),
    stageLabel: event.stageName ?? '',
  };
}

export interface CharityEventsTableProps {
  readonly charityName: string;
  readonly events: readonly CharityEvent[];
}

/**
 * The events a charity was on, newest first, searchable and sortable.
 * Presentational: the rows come in as props.
 *
 * @param props - The charity's name (for copy) and its events.
 * @returns The table, or an empty state.
 */
export function CharityEventsTable({ charityName, events }: CharityEventsTableProps): React.ReactElement {
  const navigate = useNavigate();
  const { search, setSearch } = useUrlSearch();
  const { sort, onSort } = useTableSort(CHARITY_EVENT_SORT_KEYS, DEFAULT_CHARITY_EVENT_SORT);
  const rows = sortRows(filterRows(events.map(charityEventRow), search, ['eventLabel', 'programName', 'locationLabel', 'stageLabel']), sort);

  if (events.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<CalendarDaysIcon aria-hidden className="size-6" />}
          title="No events yet"
          message={`Events that use ${charityName} show up here, from Salesforce and from each event's charity plan.`}
        />
      </Panel>
    );
  }

  return (
    <div className="space-y-3">
      <SearchInput label="Search this charity's events" placeholder="Contract, client, city or stage" value={search} onChange={(event) => setSearch(event.target.value)} className="w-full max-w-sm" />
      {rows.length === 0 ? (
        <Panel>
          <EmptyState icon={<CalendarDaysIcon aria-hidden className="size-6" />} title="No events match" message="Try another contract number, client, city or stage." />
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Date', sortKey: CHARITY_EVENT_SORT_FIELDS.date },
            { label: 'Event', sortKey: CHARITY_EVENT_SORT_FIELDS.event },
            { label: 'Location', sortKey: CHARITY_EVENT_SORT_FIELDS.location },
            { label: 'Stage', sortKey: CHARITY_EVENT_SORT_FIELDS.stage },
            'Charity outcome',
          ]}
          sort={sort}
          onSort={onSort}
          minWidth={760}
          cards={rows.map((row) => ({
            key: row.eventId,
            title: row.eventLabel,
            subtitle: row.programName ?? undefined,
            fields: [
              { label: 'Date', value: row.dateLabel },
              { label: 'Location', value: row.locationLabel || '—' },
              { label: 'Stage', value: row.stageName ? <Pill tone={eventStageTone(row.stageName)}>{row.stageName}</Pill> : '—' },
              { label: 'Charity outcome', value: row.outcome ? <Pill tone={charityOutcomeTone(row.outcome)}>{CHARITY_OUTCOME_LABELS[row.outcome]}</Pill> : '—' },
            ],
            onOpen: () => navigate(`/events/${row.eventId}`),
          }))}
        >
          {rows.map((row) => (
            <tr key={row.eventId} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small whitespace-nowrap tabular-nums">{row.dateLabel}</td>
              <td className="p-3 text-portal-small">
                <Link to={`/events/${row.eventId}`} className="font-bold text-brand hover:underline">
                  {row.eventLabel}
                </Link>
                {row.programName ? <span className="block text-portal-micro text-portal-muted">{row.programName}</span> : null}
              </td>
              <td className="p-3 text-portal-small text-portal-muted">{row.locationLabel || '—'}</td>
              <td className="p-3 text-portal-small">{row.stageName ? <Pill tone={eventStageTone(row.stageName)}>{row.stageName}</Pill> : <span className="text-portal-muted">—</span>}</td>
              <td className="p-3 text-portal-small">
                {row.outcome ? <Pill tone={charityOutcomeTone(row.outcome)}>{CHARITY_OUTCOME_LABELS[row.outcome]}</Pill> : <span className="text-portal-muted">—</span>}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}

/**
 * The charity's event history section: loads its events and renders the table.
 *
 * @param props - The charity's id and name.
 * @returns The section.
 */
export function CharityEventHistory({ charityId, charityName }: { readonly charityId: string; readonly charityName: string }): React.ReactElement {
  const events = useQuery({ queryKey: ['charity-events', charityId], queryFn: ({ signal }) => listCharityEvents(charityId, signal) });
  return (
    <section aria-labelledby="charity-events-heading" className="space-y-3">
      <h2 id="charity-events-heading" className="text-portal-card-title font-bold">
        Events
      </h2>
      {events.isPending ? (
        <SkeletonTable rows={4} columns={['w-[14%]', 'w-[36%]', 'w-[20%]', 'w-[14%]', 'w-[14%]']} label="Loading events…" />
      ) : events.isError ? (
        <ErrorNotice title="Events unavailable" message={errorMessage(events.error)} />
      ) : (
        <CharityEventsTable charityName={charityName} events={events.data} />
      )}
    </section>
  );
}
